import React, { memo, useCallback, useMemo } from 'react';
import { TMProviderInvoiceItemDetailedResponse } from '@business/entities/tm/TMProviderInvoiceResponse';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { PayablesListBloc } from './PayablesListBloc';
import { useBloc } from '@core/utils/bloc';
import { Formatter } from '@core/utils/formatter';
import { EmptySection } from '@core/components/EmptySection';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { useHistory } from '@core/router';
import { openTimeMaterialOverlay } from '@modules/timeMaterials/TimeMaterialOverlay';
import { R } from '@core/utils/r';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { CommonService } from '@business/services';
import { FormSelect } from '@core/components/form';
import { Button } from '@core/components/Button';
import { openAddEditProviderBillModal } from '../AddEditProviderBill';
import { CBody } from '@modules/common/CBody';
import { Tabs, TabsMapping } from '@modules/common';
import { PaymentType } from '@business/entities/tm';
import { TimeMaterialService } from '@business/services/time_material_service';

export const definePayablesFilterForm = () => {
  return formGroup({
    providerId: formControl<string>({ placeholder: 'Select Vendor' }),
  });
};

export type PayablesFilterFormData = FormDataType<typeof definePayablesFilterForm>;
export type PayablesFilterFormType = ReturnType<typeof definePayablesFilterForm>;

export interface PayablesListViaTypeProps {
  type: PaymentType | 'all';
  bloc: PayablesListBloc;
}

export const PayablesListViaType = memo((props: PayablesListViaTypeProps) => {
  const { bloc, type } = props;
  const form = useMemo(() => definePayablesFilterForm(), []);
  const formValue = useObservable(form.value$);
  const items = useNonNilObservable(bloc.items$);
  const providers = useNonNilObservable(bloc.providers$);

  const loading = useLoadingState(bloc.loading);

  const providerItems = useMemo(() => CommonService.selectItems(providers, 'name', 'id'), [providers]);

  const columns: GCColumn<TMProviderInvoiceItemDetailedResponse>[] = [
    {
      name: 'Vendor',
      sortField: 'provider.name',
      sortable: true,
      cell: v => v.provider.name,
    },
    {
      name: 'Category',
      sortField: 'group.name',
      sortable: true,
      cell: v => v.group.name,
    },
    {
      name: 'Project',
      sortField: 'tm.name',
      width: '10%',
      sortable: true,
      cell: v => (
        <a
          onClick={() =>
            openTimeMaterialOverlay({
              orderId: v.tm.id,
            })
          }
        >
          {v.tm.name}
        </a>
      ),
    },
    {
      name: 'Invoice Date',
      sortField: 'date',
      width: '10%',
      sortable: true,
      cell: v => Formatter.formatTime(v.date),
    },
    {
      name: 'QBO Handle/ Name',
      sortField: 'w9',
      width: '20%',
      sortable: true,
      cell: v => v.provider.w9 ?? v.provider.name,
    },
    {
      name: 'Amount',
      sortField: 'amount',
      sortable: true,
      cell: v => Formatter.formatCurrency(v.amount, 'USD'),
    },
    {
      name: 'Created At',
      sortField: 'createdAt',
      width: '10%',
      sortable: true,
      cell: v => Formatter.formatTime(v.createdAt),
    },
    {
      name: 'Reference',
      sortField: 'refNo',
      sortable: true,
      cell: v => v.refNo,
    },
  ];

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [toggledClearRows, setToggleClearSelectedRows] = React.useState(false);

  const onSelectedRowsChange = useCallback(({ selectedRows }) => {
    const selectedPayables = R.sortBy(selectedRows, 'name');
    const ids = selectedPayables.map(t => t.id);
    setSelectedIds(ids);
    setToggleClearSelectedRows(false);
  }, []);

  return (
    <EmptySection
      items={items}
      loading={loading.loading}
      icon="bx bx-book"
      title={`No Payables for ${TimeMaterialService.getPaymentTypeStatusTitle(type)}`}
      description={"You don't have any payables, please check back later."}
    >
      <div>
        <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
          <div style={{ minWidth: '300px', maxWidth: '300px', marginRight: '10px' }}>
            <FormSelect
              items={providerItems}
              isClearable
              withoutGroup
              control={form.controls.providerId}
              onChange={providerId => {
                bloc.paginated.patchQuery({ ...formValue, providerId, page: 1 }).subscribe();
              }}
            />
          </div>
          {selectedIds.length > 0 && (
            <Button
              intent="primary"
              onClick={() => {
                const selectedItems = items.filter(item => selectedIds.includes(item.id));
                openAddEditProviderBillModal({
                  providerId: formValue.providerId,
                  items: selectedItems,
                  onDone: () => bloc.paginated.reload().subscribe(),
                });
              }}
            >
              Create Bill ({selectedIds.length})
            </Button>
          )}
        </div>
        <GCTable
          selectableRows
          selectableRowsHighlight
          onSelectedRowsChange={onSelectedRowsChange}
          clearSelectedRows={toggledClearRows}
          noSearch
          noHeader
          sortServer
          columns={columns}
          data={items}
          paginated={bloc.paginated}
          paginationRowsPerPageOptions={[25, 50, 100]}
          noQueryOnSearch
        />
      </div>
    </EmptySection>
  );
});
